import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../../utils/echarts'
import type { TFunction } from 'i18next'
import { Icon } from '../../../../atoms/Icon'
import type { EmploymentTrend } from '../../employmentService'

interface EmploymentTrendChartProps {
  data: EmploymentTrend[]
  height?: number
  showFullTime?: boolean
  t: TFunction
}

export interface EmploymentTrendChartRef {
  exportChart: () => string | null
}

export const EmploymentTrendChart = forwardRef<
  EmploymentTrendChartRef,
  EmploymentTrendChartProps
>(({ data, height = 400, showFullTime = false, t }, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return

    // Initialize chart
    chartInstance.current = safeChartInit(chartRef.current)

    // Sort data by year
    const sortedData = [...data].sort((a, b) => a.year - b.year)

    // Prepare data arrays
    const years = sortedData.map((d) => d.year.toString())
    const totalEmployment = sortedData.map((d) => d.total_employment)
    const fullTimeEmployment = sortedData.map(
      (d) => d.total_employment_full_time
    )
    sortedData.map((d) => d.total_establishments)

    // Calculate growth rates for annotations
    totalEmployment.map((current, index) => {
      if (index === 0) return 0
      const previous = totalEmployment[index - 1]
      return previous > 0 ? ((current - previous) / previous) * 100 : 0
    })

    const series: any[] = [
      {
        name: t('charts.employmentTrend.legend.totalEmployment'),
        type: 'line',
        data: totalEmployment,
        lineStyle: {
          color: '#3b82f6',
          width: 3,
        },
        itemStyle: {
          color: '#3b82f6',
        },
        symbol: 'circle',
        symbolSize: 6,
        emphasis: {
          itemStyle: {
            borderColor: '#3b82f6',
            borderWidth: 2,
          },
        },
      },
    ]

    if (showFullTime) {
      series.push({
        name: t('charts.employmentTrend.legend.fullTimeEmployment'),
        type: 'line',
        data: fullTimeEmployment,
        lineStyle: {
          color: '#10b981',
          width: 2,
        },
        itemStyle: {
          color: '#10b981',
        },
        symbol: 'circle',
        symbolSize: 4,
      })
    }

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
        },
        formatter: function (params: any) {
          const year = params[0].axisValue
          const totalEmploymentLabel = t(
            'charts.employmentTrend.legend.totalEmployment'
          )
          const fullTimeLabel = t(
            'charts.employmentTrend.legend.fullTimeEmployment'
          )
          const total =
            params.find((p: any) => p.seriesName === totalEmploymentLabel)
              ?.value || 0
          const fullTime =
            params.find((p: any) => p.seriesName === fullTimeLabel)?.value || 0

          const ftRatio =
            total > 0 ? ((fullTime / total) * 100).toFixed(1) : '0'

          let tooltipContent = `
            <div style="text-align: left;">
              <strong>${t('charts.employmentTrend.tooltips.year')}: ${year}</strong><br/>
              <span style="color: #3b82f6;">● ${t('charts.employmentTrend.tooltips.totalJobs')}: ${total.toLocaleString()}</span><br/>
          `

          if (showFullTime) {
            tooltipContent += `
              <span style="color: #10b981;">● ${t('charts.employmentTrend.tooltips.fullTime')}: ${fullTime.toLocaleString()} (${ftRatio}%)</span><br/>
            `
          }

          tooltipContent += `</div>`

          return tooltipContent
        },
      },
      legend: {
        data: showFullTime
          ? [
              t('charts.employmentTrend.legend.totalEmployment'),
              t('charts.employmentTrend.legend.fullTimeEmployment'),
            ]
          : [t('charts.employmentTrend.legend.totalEmployment')],
        top: 10,
        textStyle: {
          fontSize: 12,
        },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: years,
        axisLabel: {
          fontSize: 11,
        },
        axisLine: {
          lineStyle: {
            color: '#e5e7eb',
          },
        },
      },
      yAxis: {
        type: 'value',
        name: t('charts.employmentTrend.axis.employment'),
        axisLabel: {
          fontSize: 11,
          formatter: function (value: number) {
            if (value >= 1000) {
              return (value / 1000).toFixed(1) + 'k'
            }
            return value.toString()
          },
        },
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        splitLine: {
          lineStyle: {
            color: '#f3f4f6',
            type: 'dashed',
          },
        },
      },
      series: series,
    }

    chartInstance.current.setOption(option)

    // Handle resize
    const handleResize = () => {
      chartInstance.current?.resize()
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [data, height, showFullTime, t])

  // Calculate key insights
  if (!data || data.length === 0) {
    return (
      <div
        style={{
          height: `${height}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280',
        }}
      >
        {t('charts.employmentTrend.noData')}
      </div>
    )
  }

  const sortedData = [...data].sort((a, b) => a.year - b.year)
  const firstYear = sortedData[0]
  const lastYear = sortedData[sortedData.length - 1]

  const totalGrowth =
    firstYear.total_employment > 0
      ? ((lastYear.total_employment - firstYear.total_employment) /
          firstYear.total_employment) *
        100
      : 0

  const ftGrowth =
    firstYear.total_employment_full_time > 0
      ? ((lastYear.total_employment_full_time -
          firstYear.total_employment_full_time) /
          firstYear.total_employment_full_time) *
        100
      : 0

  const currentFTRatio =
    lastYear.total_employment > 0
      ? (lastYear.total_employment_full_time / lastYear.total_employment) * 100
      : 0

  return (
    <div>
      <div
        ref={chartRef}
        style={{
          width: '100%',
          height: `${height}px`,
          minHeight: '300px',
        }}
      />

      {/* Trend insights */}
      <div
        style={{
          marginTop: '16px',
          padding: '12px 16px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
        }}
      >
        <div
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
          }}
        >
          <Icon name="bar-chart" size="xs" />{' '}
          {t('charts.employmentTrend.summary.title', {
            years: sortedData.length,
            startYear: firstYear.year,
            endYear: lastYear.year,
          })}
        </div>
        <div
          style={{
            fontSize: '13px',
            color: '#4b5563',
            lineHeight: '1.4',
          }}
        >
          •{' '}
          <strong>{t('charts.employmentTrend.summary.totalJobGrowth')}:</strong>{' '}
          {totalGrowth >= 0 ? '+' : ''}
          {totalGrowth.toFixed(1)}% (
          {lastYear.total_employment.toLocaleString()} vs{' '}
          {firstYear.total_employment.toLocaleString()})<br />
          {showFullTime && (
            <>
              •{' '}
              <strong>
                {t('charts.employmentTrend.summary.fullTimeGrowth')}:
              </strong>{' '}
              {ftGrowth >= 0 ? '+' : ''}
              {ftGrowth.toFixed(1)}%<br />•{' '}
              <strong>
                {t('charts.employmentTrend.summary.currentFTRatio')}:
              </strong>{' '}
              {currentFTRatio.toFixed(1)}%{' '}
              {t('charts.employmentTrend.summary.ofJobsAreFullTime')}
              <br />
            </>
          )}
          •{' '}
          <strong>
            {t('charts.employmentTrend.summary.jobMarketStatus')}:
          </strong>{' '}
          {totalGrowth > 5
            ? t('charts.employmentTrend.summary.rapidGrowth')
            : totalGrowth > 2
              ? t('charts.employmentTrend.summary.growing')
              : totalGrowth > 0
                ? t('charts.employmentTrend.summary.stableGrowth')
                : t('charts.employmentTrend.summary.declining')}
          <br />• <strong>{t('charts.employmentTrend.summary.cagr')}:</strong>{' '}
          {(
            (Math.pow(1 + totalGrowth / 100, 1 / sortedData.length) - 1) *
            100
          ).toFixed(2)}
          % {t('charts.employmentTrend.summary.annualCompoundGrowth')}
        </div>
      </div>
    </div>
  )
})

EmploymentTrendChart.displayName = 'EmploymentTrendChart'
